import {TRUE} from 'ol/functions';
import Cluster from 'ol/source/Cluster';
import { Vector as VectorSource } from 'ol/source'
import { SelectEvent, default as Select } from 'ol/interaction/Select';
import { isDefined, isArray, isEmpty } from '@config/utils';
import { SPIDERFIER_PROPERTY, spiderfierOverlayLayerProperty } from '@/components/maps/ol/interaction/ClusterSpiderfier';

/**
 * @enum {string}
 */
export const SelectFeatureEventType = {
	/**
	 * Triggered when feature(s) has been (de)selected.
	 * @event SelectFeatureEventType#feature
	 * @api
	 */
	SELECT_FEATURE: 'select:feature',

	/**
	 * Triggered when feature(s) has been (de)selected.
	 * @event SelectFeatureEventType#feature
	 * @api
	 */
	DESELECT_FEATURE: 'deselect:feature',

	/**
	 * Triggered when feature(s) has been (de)selected.
	 * @event SelectFeatureEventType#select
	 * @api
	 */
	SELECT: 'select:select',
};

/**
 * @classdesc
 * Events emitted by {@link module:ol/interaction/Select~Select} instances are instances of
 * this type.
 */
export class SelectFeatureEvent extends SelectEvent {
	/**
	 * @param {SelectFeatureEventType} type The event type.
	 * * @param {Array<import("../Feature.js").default>} selected Selected features.
	 * @param {Array<import("../Feature.js").default>} deselected Deselected features.
	 * * @param {import("../MapBrowserEvent.js").default} mapBrowserEvent Associated
	 *     {@link module:ol/MapBrowserEvent}.
	 * @param {import("../Feature.js").default} selected Selected feature.
	 *     {@link module:ol/MapBrowserEvent}.
	 */
	constructor(type, selected, deselected, mapBrowserEvent, feature, zoom = false, detail = false, padding_view = [0, 0, 0, 0]) {
		super(type, selected, deselected, mapBrowserEvent);

		/**
		 * Selected feature.
		 * @type {import("../Feature.js").default}
		 * @api
		 */
		this.feature = feature;

		/**
		 * Zoom to Selected Feature
		 * @type {boolean}
		 * @api
		 */
		this.zoom = zoom;

		/**
		 * Show detail of Selected Feature
		 * @type {boolean}
		 * @api
		 */
		this.detail = detail;

		/**
		 * Padding (in pixels) to be cleared inside the view. Values in the array are top, right, bottom and left padding.
		 * @type {array}
		 * @api
		 */
		this.padding_view = padding_view;
	}
}


/**
 * @constructor
 * @extends {ol.interaction.Select}
 */
class SelectInteraction extends Select {
	/**
	 * @param {Options} [opt_options] Options.
	 */
	constructor(opt_options) {
		const options = opt_options ? opt_options : {};

		options.style = options.style !== undefined ? options.style : null;

		super(options);

		this.selectedProperty_ = options.selectedProperty || 'selected';
		this.scale_ = options.scale || .5;
		this.scaleSelected_ = options.scaleSelected || 1;
		this.zindexSelected_ = options.zindexSelected || 9999999999;
		this.zoomToFeature_ = options.zoomToFeature ?? false;
		this.detailFeature_ = options.detailFeature ?? false;
		this.spiderfier_property = SPIDERFIER_PROPERTY;

		/**
		 * @private
		 * @type {FilterFunction}
		 */
		this.filter_ = options.filter || this._filterEvent;

		let layerFilter;
		if (options.layers) {
			if (typeof options.layers === 'function') {
				layerFilter = options.layers;
			} else {
				const layers = options.layers;
				layerFilter = function (layer) {
					let isSpiderfier = !!layer.get(spiderfierOverlayLayerProperty());
					return isSpiderfier || layers.indexOf(layer) >= 0;
				};
			}
		} else {
			layerFilter = TRUE;
		}

		this.layerFilter_ = layerFilter;

		this.addEventListener(
			'select',
			this._onSelect
		);
		this.addEventListener(
			'select:select',
			this._onSelect
		);
	}

	// returns true if the feature may be selected or false otherwise.
	_filterEvent(feature, layer) {
		let source = layer ? layer.getSource() : null;
		if (!source ||
			source && (source instanceof Cluster || source instanceof VectorSource)) {
			const features = feature.get('features');
			if (features) {
				if (features.length == 1) {
					feature = features[0];
					let sameFeature = this.getFeatures().getArray().filter(f => (f.get('features') ? f.get('features')[0] : f).getId() == feature.getId()).length
					return !sameFeature && feature.getKeys().includes('visible') && feature.get('visible') || !feature.getKeys().includes('visible');
				} else {
					return !(features.length > 1);
				}
			} else {
				return !feature.get(this.spiderfier_property);
			}
		}
		return false;
	}

	_onSelect(evt) {
		let selected = evt.selected ?? [];
		let deselected = evt.deselected ?? [];

		deselected.forEach((feat) => {
			if (!feat.get(this.spiderfier_property)) {
				let _feature = feat.get('features')
					&& feat.get('features').length == 1 ?
					feat.get('features')[0] : feat;
				this._applyStyle(_feature, false);
				this.fireDeselectedFeature(evt, _feature);
			}
		});

		selected.forEach((feat) => {
			if (!feat.get(this.spiderfier_property)) {
				let _feature = feat.get('features')
					&& feat.get('features').length == 1 ?
					feat.get('features')[0] : feat;
				this._applyStyle(_feature, true);
				this.fireSelectedFeature(evt, _feature);
			}
		});
	}

	fireSelectedFeature(evt, feature) {
		this.dispatchEvent(
			new SelectFeatureEvent(
				SelectFeatureEventType.SELECT_FEATURE,
				[feature],
				evt.deselected,
				evt.mapBrowserEvent,
				feature,
				evt.zoom ?? this.zoomToFeature_,
				evt.detail ?? this.detailFeature_,
				evt.padding_view,
			)
		);
	}

	fireDeselectedFeature(evt, feature) {
		if (evt.type != SelectFeatureEventType.SELECT) {
			this.dispatchEvent(
				new SelectFeatureEvent(
					SelectFeatureEventType.DESELECT_FEATURE,
					evt.selected,
					[feature],
					evt.mapBrowserEvent,
					feature
				)
			);
		}
	}

	_applyStyle(feature, selected = true) {
		// [TODO] razao desta condição ????
		// if (this.getStyle()) return;

		var styles = null;
		var styleFunction = feature.getStyleFunction();
		if (isDefined(styleFunction)) {
			let map = this.getMap();
			let viewRes = map.getView().getResolution();
			styles = styleFunction(feature, viewRes);
		} else {
			styles = feature.getStyle();
		}
		if (!isArray(styles)) {
			styles = [styles];
		}
		if (!isEmpty(styles)) {
			styles.forEach((style, j) => {
				let img = style && style.getImage ? style.getImage() : null;
				if (img) {
					img.setScale(selected ? this.scaleSelected_ : this.scale_);
					if (selected) {
						feature.set('zIndex', style.getZIndex());
					}
					let zIndex = selected ? this.zindexSelected_ : feature.get('zIndex');
					style.setZIndex(zIndex);
				}
			});
			feature.setStyle(styleFunction);
		}

		feature.set(this.selectedProperty_, selected);

		// let style = feature.getStyle();
		// let img = style && style.getImage ? style.getImage() : null;
		// if(img){
		//   img.setScale(selected ? this.scaleSelected_ : this.scale_);
		//   if(selected){
		//     feature.set('zIndex', style.getZIndex());
		//   }
		//   let zIndex = selected ? this.zindexSelected_ : feature.get('zIndex');
		//   style.setZIndex(zIndex);
		// }
		// feature.setStyle(style);
	}

	/**
	 * Method: select
	 *
	 * Parameters:
	 * feature - {<OpenLayers.Feature.Vector>}
	 */
	select({ feature, zoom = true, detail = false, padding_view = [0, 0, 0, 0] }) {
		const features = this.getFeatures();
		const selected = [];
		const deselected = [];
		for (let i = features.getLength() - 1; i >= 0; --i) {
			const feat = features.item(i);
			features.remove(feat);
			deselected.push(feat);
		}
		selected.push(feature);
		features.extend(selected);

		if (selected.length > 0 || deselected.length > 0) {
			this.dispatchEvent(
				new SelectFeatureEvent(
					SelectFeatureEventType.SELECT,
					selected,
					deselected,
					null,
					null,
					zoom,
					detail,
					padding_view,
				)
			);
		}
	}

	/**
	 * Method: unselect
	 *
	 * Parameters:
	 * feature - {<OpenLayers.Feature.Vector>}
	 */
	unselect(feature) {
		const features = this.getFeatures();
		const featuresArray = this.getFeatures().getArray();
		const idx = featuresArray.indexOf(feature);
		if (idx > -1) {
			features.remove(feature);
			this.dispatchEvent(
				new SelectFeatureEvent(
					SelectFeatureEventType.DESELECT_FEATURE,
					featuresArray,
					[feature],
					null,
					feature,
				)
			);
		}
	}

	/**
	* Method: unselectAll
	* Unselect all selected features.
	*/
	unselectAll() {
		const features = this.getFeatures();
		const selected = [];
		const deselected = [];
		for (let i = features.getLength() - 1; i >= 0; --i) {
			const feature = features.item(i);
			features.remove(feature);
			deselected.push(feature);
		}

		if (deselected.length > 0) {
			this.dispatchEvent(
				new SelectFeatureEvent(
					SelectFeatureEventType.SELECT,
					selected,
					deselected
				)
			);
		}
	}
};

export default SelectInteraction;